import { Component, Input, SimpleChanges } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { debounceTime, Subject, takeUntil } from "rxjs";
import { IGenericSelectDto } from "../../models/Interfaces";
import { GroupedProductRelationDto, IProductList, ProductListFilter, RelatedProductsTypeEnum } from "../../models/product-v2";
import { BrandService } from "../../pages/members/brand/services/brand.service";
import { CategoryV2Service } from "../../pages/members/category-v2/services/category.v2.service";
import { MessageService } from "../../util/message.service";
import { CategoryLevelEnum } from "../../models/category.v2";
import { ProductV2Service } from "../../pages/members/product-v2/services/product.v2.service";

@Component({
    selector: 'related-products-table',
    templateUrl: 'related-products-table.component.html',
    styleUrls: ['related-products-table.component.scss']
})
export class RelatedProductTableComponent {

    @Input() storeId: string = '';
    @Input() groupedProductRelationDto: GroupedProductRelationDto[] = []
    id: string = '';
    products: IProductList[] = [];

    destroy$ = new Subject<any>();
    imageLoaded: boolean[] = [];
    tab: number = 1;
    cross_tab: number = 1
    up_tab: number = 0
    total: number = 0;

    categories: IGenericSelectDto<string>[] = [];
    categorySearch$ = new Subject<string>();
    categorySearchTerm = '';
    selectedCategory: string = '';
    categoryPage = 1;
    hasMoreCategories = true;
    isLoadingMoreCategories = false;

    brands: IGenericSelectDto<string>[] = [];
    brandSearch$ = new Subject<string>();
    brandSearchTerm = '';
    selectedBrand: string = '';
    brandPage = 1;
    hasMoreBrands = true;
    isLoadingMoreBrands = false;

    selectedProductIds: string[] = [];
    private productsLoaded = false;
    private lastProductFilter: ProductListFilter | null = null;
    currentPage = 1;

    previousCategory: string | null = null;
    previousBrand: string | null = null;

    crosssellingsProducts: GroupedProductRelationDto = {
        type: RelatedProductsTypeEnum.CROSSSELLING,
        relatedSkus: []
    };
    upsellingsProducts: GroupedProductRelationDto = {
        type: RelatedProductsTypeEnum.UPSELLING,
        relatedSkus: []
    };

    constructor(
        private readonly _categoryService: CategoryV2Service,
        private readonly _productService: ProductV2Service,
        private readonly _brandService: BrandService,
        private readonly _messageService: MessageService,
        private readonly route: ActivatedRoute,
        private readonly _route: Router,
    ) {

    }

    ngOnDestroy(): void {
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['groupedProductRelationDto'] && changes['groupedProductRelationDto'].currentValue) {
            this.initProductRelations();
        }
    }

    private initProductRelations() {
        console.log('groupedProductRelationDto', this.groupedProductRelationDto)

        this.groupedProductRelationDto.forEach(x => {
            if (x.type === RelatedProductsTypeEnum.CROSSSELLING) {
                this.crosssellingsProducts = x;
            } else if (x.type === RelatedProductsTypeEnum.UPSELLING) {
                this.upsellingsProducts = x;
            }
        });
    }

    ngOnInit() {
        this.initData();
        this.categorySearch$
            .pipe(debounceTime(400), takeUntil(this.destroy$))
            .subscribe(term => {
                this.categorySearchTerm = term;
                this.categoryPage = 0;
                this.hasMoreCategories = true;

                this._categoryService.list(this.storeId, {
                    size: 10,
                    page: 0,
                    name: term,
                    level: CategoryLevelEnum.LEAF
                }).pipe(takeUntil(this.destroy$))
                    .subscribe(res => {
                        this.categories = res.items.map(x => ({
                            key: x.shortName,
                            value: x.id
                        }));

                        this.hasMoreCategories = this.categories.length < res.page.totalElements;
                    });
            });

        this.brandSearch$
            .pipe(debounceTime(800), takeUntil(this.destroy$))
            .subscribe(term => {
                this.brandSearchTerm = term;
                this.brandPage = 0;
                this.hasMoreBrands = true;

                this._brandService.list({
                    storeId: this.storeId,
                    page: 0,
                    size: 10,
                    name: term
                })
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(res => {
                        this.brands = res.items.map(x => ({
                            key: x.name,
                            value: x.id
                        }));

                        this.hasMoreBrands = this.brands.length < res.page.totalElements;
                    });
            });
    }

    initData() {

        this.route.params.subscribe({
            next: (params) => {
                if (params['productId']) {
                    this.id = params['productId'];
                }
            }
        });
        if (!this.productsLoaded) {
            this.productsLoaded = true;
            this._productService.list(this.storeId, { page: 0, size: 27, status: 'ACTIVE' }).subscribe({
                next: (res) => {
                    if (res) {
                        this.products = res.items;
                        this.total = res.page.totalElements;
                    }
                }
            })



            this._categoryService.list(this.storeId, {
                size: 10,
                page: 0,
                level: CategoryLevelEnum.LEAF
            })
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (res) => {
                        this.categories = res.items.map(x => ({
                            key: x.shortName,
                            value: x.id
                        }))
                    }
                })

            this._brandService.list({
                storeId: this.storeId,
                page: 0,
                size: 10
            })
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (res) => {
                        this.brands = res.items.map(x => ({
                            key: x.name,
                            value: x.id
                        }))
                    }
                })
        }
    }

    selectPage(page: number) {
        let newParams: ProductListFilter = {
            page: page - 1,
            size: 27,
            brandId: this.selectedBrand,
            categoryId: this.selectedCategory,
            status: 'ACTIVE'
        }

        const filterChanged = JSON.stringify({ ...newParams, page: undefined }) !==
            JSON.stringify({ ...this.lastProductFilter, page: undefined });
        const pageChanged = page !== this.currentPage;

        // Evita la llamada innecesaria
        if (!filterChanged && !pageChanged) {
            return;
        }

        this.lastProductFilter = newParams;
        this.currentPage = page;

        this._productService.list(this.storeId, newParams).subscribe({
            next: (res) => {
                if (res) {
                    this.products = res.items;
                    this.total = res.page.totalElements;
                }
            }
        })
    }

    onCategorySelect($event: IGenericSelectDto<string>) {
        if (this.previousCategory === $event.value) return;

        this.previousCategory = $event.value;
        this.selectedCategory = $event.value;
        this.selectPage(1);
    }

    loadMoreCategories() {
        if (this.isLoadingMoreCategories || !this.hasMoreCategories) return;

        this.isLoadingMoreCategories = true;

        this._categoryService.list(this.storeId, {
            size: 10,
            page: this.categoryPage,
            name: this.categorySearchTerm,
            level: CategoryLevelEnum.LEAF
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    const newCategories = res.items.map(x => ({
                        key: x.shortName,
                        value: x.id
                    }));

                    this.categories = [...this.categories, ...newCategories];

                    const total = res.page.totalElements;
                    this.hasMoreCategories = this.categories.length < total;
                    if (this.hasMoreCategories) {
                        this.categoryPage++;
                    }

                    this.isLoadingMoreCategories = false;
                },
                error: () => {
                    this.isLoadingMoreCategories = false;
                }
            });
    }

    onCategorySearch(term: string) {
        this.categorySearch$.next(term);
    }

    clearCategoryFilter() {
        if (!this.selectedCategory) return;

        this.selectedCategory = '';
        this.previousCategory = null;
        this.selectPage(1);
    }

    onBrandSelect($event: IGenericSelectDto<string>) {
        if (this.previousBrand === $event.value) return;

        this.previousBrand = $event.value;
        this.selectedBrand = $event.value;
        this.selectPage(1);
    }

    loadMoreBrands() {
        if (this.isLoadingMoreBrands || !this.hasMoreBrands) return;

        this.isLoadingMoreBrands = true;

        this._brandService.list({
            storeId: this.storeId,
            page: this.brandPage,
            size: 10,
            name: this.brandSearchTerm
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    const newBrands = res.items.map(x => ({
                        key: x.name,
                        value: x.id
                    }));

                    this.brands = [...this.brands, ...newBrands];

                    const total = res.page.totalElements;
                    this.hasMoreBrands = this.brands.length < total;

                    if (this.hasMoreBrands) {
                        this.brandPage++;
                    }

                    this.isLoadingMoreBrands = false;
                },
                error: () => {
                    this.isLoadingMoreBrands = false;
                }
            });
    }

    onBrandSearch(term: string) {
        this.brandSearch$.next(term);
    }

    clearBrandFilter() {
        if (!this.selectedBrand) return;

        this.selectedBrand = '';
        this.previousBrand = null;
        this.selectPage(1);
    }

    toggleProductSelection(productId: string): void {
        const index = this.selectedProductIds.indexOf(productId);

        if (index > -1) {
            this.selectedProductIds.splice(index, 1);
        } else {
            this.selectedProductIds.push(productId);
        }
    }

    updateCrossSellings() {
        const existingIds = this.crosssellingsProducts.relatedSkus.map(x => x.relatedProductId);
        const allProductIds = Array.from(new Set([...existingIds, ...this.selectedProductIds]));

        this._productService.addCrossSellingProducts(this.storeId, this.id, allProductIds)
            .subscribe({
                next: () => {
                    this._messageService.success('Cross-Sellings agregados correctamente.');
                    this.selectedProductIds = [];
                },
                error: (e) => {
                    console.error(e);
                    this.handleHttpError(e);
                },
                complete: () => {
                    this._route.navigate(['/members/product-v2/listProduct']);
                }
            });
    }

    updateUpSellings() {
        const existingIds = this.crosssellingsProducts.relatedSkus.map(x => x.relatedProductId);
        const allProductIds = Array.from(new Set([...existingIds, ...this.selectedProductIds]));
        this._productService.addUpSellingProducts(this.storeId, this.id, allProductIds)
            .subscribe({
                next: () => {
                    this._messageService.success('Up-Sellings agregados correctamente.');
                    this.selectedProductIds = [];
                },
                error: (e) => {
                    console.error(e);
                    this.handleHttpError(e);
                },
                complete: () => {
                    this._route.navigate(['/members/product-v2/listProduct']);
                }
            });
    }

    removeCrosssellingProduct(id: string) {
        this._productService.deleteCrossSellingProduct(this.storeId, this.id, id)
            .subscribe({
                next: () => {
                    this._messageService.success('Producto removido correctamente.');
                    this.crosssellingsProducts.relatedSkus = this.crosssellingsProducts.relatedSkus.filter(x => x.id !== id);
                },
                error: (e) => {
                    console.error(e);
                    this.handleHttpError(e);
                }
            });
    }

    removeUpsellingProduct(id: string) {
        this._productService.deleteUpSellingProduct(this.storeId, this.id, id)
            .subscribe({
                next: () => {
                    this._messageService.success('Producto removido correctamente.');
                    this.upsellingsProducts.relatedSkus = this.upsellingsProducts.relatedSkus.filter(x => x.id !== id);

                },
                error: (e) => {
                    console.error(e);
                    this.handleHttpError(e);
                }
            });
    }

    private handleHttpError(error: any): void {
        const status = error?.status;
        const detail = error?.error?.detail;

        const message = status && detail
            ? `Error ${status}: ${detail}`
            : 'Ocurrió un error inesperado';

        console.error('Error HTTP:', error);
        this._messageService.error(message);
    }

    onImageError(event: Event, index: number) {
        const img = event.target as HTMLImageElement;
        img.src = 'assets/images/no-product-image.jpeg';
        this.imageLoaded[index] = true;
      }
      
} 